import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Card} from 'src/design/atoms/Card';
import {Divider} from 'src/design/atoms/Divider';
import {Flex} from 'src/design/atoms/Flex';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {Title} from 'src/design/atoms/Title';
import {
  IconCamera,
  IconCheck,
  IconExclamationCircle,
  IconSparkles,
} from '@tabler/icons-react';
import React, {useState} from 'react';
import {UserTypeEnum} from 'src/api/ProactApiClient';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {useUpdateAvatarUrlMutation} from 'src/redux/queries/user';
import {UserAvatarField} from 'src/app/personnel/personnelHelpers';

import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {getAvatarUrl} from 'src/redux/slices/sessionSlice';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';

const field: IFormFieldV2 = {
  ...UserAvatarField,
  label: '',
};

export const PendingAccountSetup: React.FC = () => {
  const {setAlert} = useAlert();

  const dispatch = useAppDispatch();
  const {avatarUrl, instance, session} = useAppSelector(state => state.session);
  const hasPendingAction = !avatarUrl || avatarUrl == '[]';
  const isAvatarRequiredEmployees = instance?.isAvatarRequiredEmployees;
  const isAvatarRequiredContractors = instance?.isAvatarRequiredContractors;
  const userType = session?.user?.userType;
  const isAvatarUploadEnabled =
    (userType === UserTypeEnum.Contractor &&
      isAvatarRequiredContractors === true) ||
    (userType === UserTypeEnum.Employee && isAvatarRequiredEmployees === true);
  const showAvatarUpload = hasPendingAction && isAvatarUploadEnabled;

  const [updateAvatarUrl] = useUpdateAvatarUrlMutation();

  const [isSavePending, setIsSavePending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [value, setValue] = useState<string | undefined>('');

  const handleAvatarChange = (name: string, newValue: string): void => {
    setValue(newValue);
  };

  const handleSaveAvatar = async function () {
    try {
      if (!value) {
        return;
      }

      setIsSavePending(true);

      await updateAvatarUrl({
        avatarUrl: value,
      }).unwrap();

      setAlert('Avatar was successfully updated', 'success');
      setSuccess(true);

      void dispatch(getAvatarUrl());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  if (!showAvatarUpload && !success) {
    return null;
  }

  return (
    <Card shadow="sm" radius="md" withBorder p={0}>
      <Box
        p="md"
        style={{borderBottom: '1px solid var(--mantine-color-gray-2)'}}>
        <Flex align="center" gap="sm">
          <ThemeIcon
            size="md"
            radius="md"
            variant="light"
            color={success ? 'green' : 'yellow'}>
            {success ? <IconSparkles size={16} /> : <IconCamera size={16} />}
          </ThemeIcon>
          <Title order={5}>
            {success ? 'All Set!' : 'Complete Your Profile'}
          </Title>
        </Flex>
      </Box>

      <Box p="md">
        {success ? (
          <Alert
            title="You're all caught up!"
            color="green"
            variant="light"
            icon={<IconCheck size={18} />}
            radius="md">
            Your account is now ready to use.
          </Alert>
        ) : (
          <Stack gap="md">
            <Alert
              title="Welcome to Proact!"
              color="yellow"
              variant="light"
              icon={<IconExclamationCircle size={18} />}
              radius="md">
              <Text size="sm" mb="sm">
                To complete your account setup, please upload a passport-style
                photo. Requirements:
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>No hats or glasses</List.Item>
                <List.Item>White or off-white background</List.Item>
                <List.Item>Recent color photo (within 6 months)</List.Item>
                <List.Item>No scans of existing photos</List.Item>
              </List>
            </Alert>

            <Divider />

            <FormFileUploadV2
              field={field}
              value={value}
              onChange={handleAvatarChange}
            />

            <Group justify="flex-end">
              <Button
                disabled={!value}
                loading={isSavePending}
                onClick={handleSaveAvatar}>
                Save Photo
              </Button>
            </Group>
          </Stack>
        )}
      </Box>
    </Card>
  );
};
